import { Injectable, Inject, PLATFORM_ID, Injector } from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { CachingService } from './modules/shared/services/caching.service';

@Injectable()
export class Configuration {
  public CDNServer = environment.CDN_URL;
  public GanttExportURL = environment.GANTT_EXPORT_URL;
  public ApiVersion = environment.API_VERSION;
  public configuration = environment.CONFIGURATION;
  public readonly ForbiddenInputCharacters = [';', '<', '>', '!'];
  private maxFileSize = 0;
  private allowedUploadTypes = '';
  private resourceFolderName = 'Resource';
  private readonly map_provider = environment.mapProvider;
  private readonly google_maps_config = environment.google_maps;

  // Private backing field for BackEndAPI - MUST be null to allow cache check on first access
  private _backendAPI: string = null;

  // Lazy-loaded CachingService to avoid circular dependency
  private _cachingService: CachingService;

  /**
   * Getter for BackEndAPI that checks cache before falling back to environment default.
   * This ensures the backend URL persists across page refreshes.
   */
  public get BackEndAPI(): string {
    // Priority 1: Return in-memory cache if already loaded
    // (Optimization for subsequent calls in the same session)
    if (this._backendAPI) {
      console.log('Configuration.BackEndAPI - Using in-memory cached URL:', this._backendAPI);
      return this._backendAPI;
    }

    // Priority 2: Check persistent cache from previous session
    // (This is what makes it survive page refresh!)
    const cachedUrl = this.cachingService?.getBackendAPIUrl();
    if (cachedUrl) {
      // Cache in memory for faster subsequent access
      this._backendAPI = cachedUrl;
      console.log('Configuration.BackEndAPI - Loaded from IndexedDB cache:', cachedUrl);
      return cachedUrl;
    }

    // Priority 3: Fall back to environment default
    // (Only used on first login or after logout)
    console.log('Configuration.BackEndAPI - Using environment default:', environment.API_URL);
    return environment.API_URL;
  }

  public get MaxFileSize() {
    return this.maxFileSize;
  }

  public get AllowedUploadTypes() {
    return this.allowedUploadTypes;
  }

  public SetMaxFileSize(maxFileSize: number) {
    this.maxFileSize = maxFileSize;
  }

  public SetAllowedUploadTypes(allowedTypes: string) {
    this.allowedUploadTypes = allowedTypes;
  }

  public SetResourceFolderName(resourceFolderName: string) {
    this.resourceFolderName = resourceFolderName;
  }

  public get ResourceFolderName() {
    return this.resourceFolderName;
  }

  public get mapProvider() {
    return this.map_provider;
  }

  public get googleMapsConfiguration() {
    return this.google_maps_config;
  }

  public Href: string;

  public mode: string = 'Prod';

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private injector: Injector
  ) {
    const href: string = isPlatformBrowser(this.platformId) ? window.location.href : 'n/a';

    this.Href = href;
  }

  /**
   * Lazy getter for CachingService to avoid circular dependency.
   * CachingService is only retrieved when needed.
   */
  private get cachingService(): CachingService {
    if (!this._cachingService) {
      this._cachingService = this.injector.get(CachingService);
    }
    return this._cachingService;
  }

  public buildEndpoint(urlPath: string): string {
    const endpoint = `${this.BackEndAPI}/${this.ApiVersion}/${urlPath}`;
    console.log(`Configuration.buildEndpoint - Building endpoint: ${endpoint}`);
    return endpoint;
  }

  public buildDocumentUrl(urlPath: string): string {
    return `${this.BackEndAPI}/${urlPath}`;
  }

  public buildStaticFileUrl(urlPath: string) {
    if (urlPath.startsWith('http')) {
      return urlPath;
    }

    return !urlPath.includes(this.resourceFolderName)
      ? `${this.BackEndAPI}/${this.resourceFolderName}/${urlPath}`
      : this.buildDocumentUrl(urlPath);
  }

  /**
   * Initializes the backend API URL and persists it to cache.
   * This ensures the URL survives page refreshes.
   * @param apiURL - The backend API URL to set, or null/undefined to use environment default
   */
  initBackendAPI(apiURL: string) {
    if (apiURL) {
      this._backendAPI = apiURL;
      this.cachingService.setBackendAPIUrl(apiURL);
    } else {
      // When we are hitting the same account into the same Deployment,
      // we don't need to seek different endpoint as we already have the correct one in environment
      this._backendAPI = environment.API_URL;
      this.cachingService.setBackendAPIUrl(environment.API_URL);
    }

    console.log('Backend API initialized:', this._backendAPI);
  }

  /**
   * Clears the backend API URL from both memory and cache.
   * Called during logout to reset to environment default.
   */
  clearBackendAPI() {
    this._backendAPI = null;
    this.cachingService.removeBackendAPIUrl();
  }
}
